import ZTable, { ITableRef } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Tooltip, Button } from 'antd';
import { TicketIcon } from '@/assets/icons/entities/_';
import TicketsModal from './modals/tickets.modal';
import { sumBy } from 'lodash';
import { BookingPagingDto, LianeBookingPagingDto } from '@/__generated';
import { IFilterItem, IColumnType } from '@/components/ZTable';
import { formatApiDate, formatDisplayFullDate } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { BookingPayModeType, BookingStatusType } from '@/constants';

export const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'BookingRefNo', label: 'ticketBooking_BookingRefNo' },
  { type: 'TEXT', name: 'ReceiptRefNo', label: 'bookingPage_ReceiptRefNo' },
  { type: 'TEXT', name: 'PassengerName', label: 'bookingPage_PassengerName' },
  { type: 'DATE', name: 'BookingDate', label: 'ticketBooking_BookingDate', parseValue: v => formatApiDate(v) },
  { type: 'DATE', name: 'departureDate', label: 'bookingPage_DepartureDate', parseValue: v => formatApiDate(v) },
];

export const columns: IColumnType<LianeBookingPagingDto>[] = [
  {
    title: 'ticketBooking_BookingRefNo',
    dataIndex: 'bookingRefNo',
    ellipsis: true,
    sorter: true,
    width: 150,
  },
  {
    title: 'bookingPage_ReceiptRefNo',
    dataIndex: 'receiptRefNo',
    sorter: true,
    ellipsis: true,
    width: 220,
  },

  {
    title: 'ticketBooking_Passenger',
    dataIndex: 'noOfPassengers',
    ellipsis: true,
    width: 100,
  },
  {
    title: 'bookingPage_DepartureDate',
    dataIndex: 'departureDateTime',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },
  {
    title: 'bookingPage_ArrivalDateTime',
    dataIndex: 'arrivalDateTime',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },
  {
    title: 'passengerBookingPage_PayMode',
    dataIndex: 'payMode',
    align: 'center',
    width: 150,
    render: v => BookingPayModeType.__getValue(v),
  },
  {
    title: 'base.Status',
    dataIndex: 'status',
    width: 150,
    align: 'center',
    render: v => BookingStatusType.__getValue(v),
  },
  {
    title: 'passengerBookingPage_TotalAmount',
    dataIndex: 'ticketAmount',
    width: 150,
    render: formatCurrency,
  },
  {
    title: 'ticketBooking_BookingDate',
    dataIndex: 'bookingDate',
    width: 170,
    ellipsis: true,
    render: v => (v ? formatDisplayFullDate(v) : ''),
    sorter: true,
  },
];

const width = sumBy(columns, x => x.width || 0);

export const PAGE_TITLE = 'passengerBookingPage_Title';

export default function PassengerBookingPage() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [displayTicketsModal, setDisplayTicketsModal] = React.useState<BookingPagingDto>();

  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<BookingPagingDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          scroll={{ x: width }}
          url="/liane-bookings/get-bookings"
          bordered
          order_by="createdOn"
          order_by_asc={false}
          allowEdit={false}
          allowDelete={false}
          filters={filters}
          searchBtnAlignLeft
          actionColumnWidth={100}
          extraActions={[
            {
              render: record => {
                return (
                  <Tooltip title={t('bookingPage_ViewTickets')}>
                    <Button
                      size="small"
                      onClick={() => {
                        setDisplayTicketsModal(record);
                      }}
                      type="link"
                      icon={<TicketIcon style={{ height: 14 }} />}
                    />
                  </Tooltip>
                );
              },
            },
          ]}
        />
      </div>
      {displayTicketsModal ? <TicketsModal booking={displayTicketsModal} onClose={() => setDisplayTicketsModal(undefined)} /> : null}
    </MainContent>
  );
}
